/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import {usePostRequest} from "../../../../../api/helper";
import {
  ConfigSensorWizardState,
  defaultDefinePropertiesStepData,
  defaultSelectSensorStepData,
  selectAdasProgramsArchive,
  selectConfigSensorWizardState,
  selectEditSensorBomState,
  selectMoveSensorLocationsState,
  selectSensorBomData,
  selectSensorBomState,
  setConfigSensorWizardState,
  setMoveLocationKey,
  setSensorBomData,
  setSensorLocations
} from "../../../../../redux/ducks/AdasPrograms/slice";
import BoxWithCenteredContent from "../../../../components/Containers/BoxWithCenteredContent";
import PopupState, {bindHover, bindPopover} from "material-ui-popup-state";
import HoverPopover from "material-ui-popup-state/HoverPopover";
import {EAdasProgramSensorBomCheckboxTypes, ISensorLocation, VEHICLE_IMAGE_SIZE} from "../types";
import ArgosDialog from "../../../../components/Dialogs/ArgosDialog";
import {ADAS_PROGRAMS_CODE_CONFIG} from "../../config";
import styled from "styled-components";
import * as COLORS from "../../../../../constants/colors"
import {getSensorIcon} from "../adasProgramsHelperFunctions";
import ArgosButton from "../../../../components/Buttons/ArgosButton";
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {ConfigSensorWizard} from "./ConfigSensorWizard/ConfigSensorWizard";
import {EAdasProgramsEditSensorDialogType} from "./types";
import {toast} from "react-hot-toast";

export const VehicleImage = () => {
  // get the page coordinates of the image and the page coordinates of the mouse, and add an element to the top left of the image with the mouse coordinates
  const [imagePosition, setImagePosition] = useState({x: 0, y: 0})
  const [imageSize, setImageSize] = useState({width: 0, height: 0})
  const imageElement = useRef<HTMLImageElement>(null)
  const [dialogOffset, setDialogOffset] = useState({x: 0, y: 0})
  const propsSensorLocations = useAppSelector(state => selectSensorBomData(state)?.data?.sensorLocations) ?? null
  const [percent, setPercent] = useState({x: 0, y: 0})
  const [pendingSensorLocation, setPendingSensorLocation] = useState<ISensorLocation>()
  const dispatch = useAppDispatch()
  const adasProgramId = useAppSelector(state => selectSensorBomData(state)?.adasProgramId)

  const editLocationsEnabled = useAppSelector(state => state.adasPrograms.isEditLocationsEnabled)
  const sensorLocations = useAppSelector(state => selectEditSensorBomState(state).sensorLocations)
  useEffect(() => {
    if (editLocationsEnabled) {
      setPendingSensorLocation(undefined)
    }
  }, [editLocationsEnabled]);

  useEffect(() => {
    dispatch(setSensorLocations(propsSensorLocations))
  }, [propsSensorLocations])

  useEffect(() => {
    const root = document.getElementById('rootRef')
    if (root) {
      const rect = root.getBoundingClientRect()
      setDialogOffset({x: rect.x, y: rect.y})
    }
  }, [])

  // get image position and size
  useEffect(() => {
    if (imageElement.current) {
      const rect = imageElement.current.getBoundingClientRect()
      setImagePosition({x: rect.x - dialogOffset.x, y: rect.y - dialogOffset.y})
      setImageSize({width: rect.width, height: rect.height})
    }
  }, [imageElement, dialogOffset])

  const addSensorLocationHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=addSensorLocation')
  const addSensorLocation = useCallback(() => {
    // replace the pending sensor location with a saved one
    if (pendingSensorLocation && adasProgramId) {
      const savedSensorLocation: ISensorLocation = {
        ...pendingSensorLocation,
        sensorLocationState: 'saved',
      }
      const newSensorLocations = {...sensorLocations, [savedSensorLocation.locationKey]: savedSensorLocation}
      // delete the pending sensor location if it exists
      delete newSensorLocations[""]
      // setSensorLocations(newSensorLocations)
      addSensorLocationHandle({sensorLocation: savedSensorLocation, adasProgramId, requestType: 'addSensorLocation'})
        .then((res) => {
          dispatch(setSensorBomData(res))
          setPendingSensorLocation(undefined)
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }, [adasProgramId, pendingSensorLocation, sensorLocations, addSensorLocationHandle])

  const deleteSensorLocationHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=deleteSensorLocation')
  const deleteSensorLocation = useCallback((locationKey: string) => {
    const location = sensorLocations?.[locationKey]
    if (location && location.sensorLocationState === 'saved' && adasProgramId) {
      deleteSensorLocationHandle({locationKey, adasProgramId})
        .then((res) => {
          dispatch(setSensorBomData(res))
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }, [sensorLocations, adasProgramId])

  const moveLocationsEnabled = useAppSelector(state => selectMoveSensorLocationsState(state).isMoveLocationsEnabled)
  const moveLocationKey = useAppSelector(state => selectMoveSensorLocationsState(state).locationKey)

  const isInputOrClickDisabled = !editLocationsEnabled && !moveLocationsEnabled

  const onClickVehicleImage = (e: any) => {
    if (isInputOrClickDisabled) return
    const relativeClick = {x: e.pageX - dialogOffset.x, y: e.pageY - dialogOffset.y}

    // get the relative position of the click
    const relativeX = relativeClick.x - imagePosition.x
    const relativeY = relativeClick.y - imagePosition.y

    // get the position of the click relative to the imagePosition which is the top left of the image and the size of the image
    const relativeXPercent = relativeX / imageSize.width
    const relativeYPercent = relativeY / imageSize.height
    const multRelativeXPercent = relativeXPercent * 100
    const multRelativeYPercent = relativeYPercent * 100
    setPercent({x: multRelativeXPercent, y: multRelativeYPercent})

    // NOTE: Right now, if you click a location to change the name, and then you click another place, then it wont save it properly. So click and edit and save without clicking elsewhere.

    // If mode is move locations, get the clicked location key and just change its coordinates and make sensorLocationState 'pending'. Else, add a new sensor location.
    if (moveLocationsEnabled && moveLocationKey) {
      const newSensorLocations = {...sensorLocations}
      newSensorLocations[moveLocationKey] = {...newSensorLocations[moveLocationKey], coordinates: {x: multRelativeXPercent, y: multRelativeYPercent}, sensorLocationState: 'saved'}
      dispatch(setSensorLocations(newSensorLocations))
      setPendingSensorLocation(newSensorLocations[moveLocationKey])
    } else {
      const plocationKey = pendingSensorLocation?.locationKey ?? ''
      const locationKey = sensorLocations?.[plocationKey]?.locationKey ?? ''
      const locationName = 'test name'
      const sensorLocation: ISensorLocation = {
        locationKey: locationKey,
        name: locationName,
        coordinates: {
          x: multRelativeXPercent,
          y: multRelativeYPercent,
        },
        sensors: [],
        sensorLocationState: 'pending'
      }
      dispatch(setSensorLocations({sensorLocationData: sensorLocation}))
      setPendingSensorLocation(sensorLocation)
    }
  }

  const setPendingSensorLocationKey = useCallback((_originalKey: string, _newKey: string) => {
    if (moveLocationsEnabled) {
      if (!sensorLocations || _newKey in sensorLocations) {
        return
      }
      const newSensorLocations = {...sensorLocations}
      // need to replace location key (which is also object key) with name
      const location = newSensorLocations[_originalKey]
      if (location) {
        const newLocation: ISensorLocation = {
          ...location,
          locationKey: _newKey,
          name: _newKey,
        }
        newSensorLocations[_newKey] = newLocation
        delete newSensorLocations[_originalKey]
        dispatch(setSensorLocations(newSensorLocations))
        dispatch(setMoveLocationKey(_newKey))
        // setPendingSensorLocation(newLocation)
      } else {
        // name probably wont be in sensorLocations, so need to
      }
    } else {
      setPendingSensorLocation((prevState: ISensorLocation | undefined) => {
        if (!prevState) return
        const newPendingSensorLocation: ISensorLocation = {
          ...prevState,
          name: _newKey,
          locationKey: _newKey,
        }
        return newPendingSensorLocation
      })
    }
  }, [moveLocationsEnabled, sensorLocations])

  const isSaveDisabled = !pendingSensorLocation || pendingSensorLocation.sensorLocationState === 'saved'

  const addSensorToLocation = useCallback((_locationKey: string, _sensorLocation: any) => {
    const wizardState: ConfigSensorWizardState = {
      action: EAdasProgramsEditSensorDialogType.ADD_FROM_LOCATION,
      selectSensorStep: {
        ...defaultSelectSensorStepData,
        locationKey: [_locationKey],
      },
      definePropertiesStep: defaultDefinePropertiesStepData,
    }
    dispatch(setConfigSensorWizardState(wizardState))
  }, [])

  const sensorsData = useAppSelector(state => selectSensorBomData(state)?.data?.sensors)
  const wizardState = useAppSelector(selectConfigSensorWizardState)
  const isEditSensorDialogOpen = wizardState !== null

  const visibleSensorLocations = useMemo(() => {
    if (!sensorLocations) return {}
    return Object.fromEntries(Object.entries(sensorLocations).filter(([key, value]) => !value.hidden))
  }, [sensorLocations])
  return (
    <>
      <BoxWithCenteredContent vertical horizontal
                              style={{width: '100%', height: 510, overflow: 'auto', paddingRight: 300}}>
        <div style={{position: 'relative', top: 0, left: 0}}>
          {ADAS_PROGRAMS_CODE_CONFIG.ADMIN_MODIFIERS &&
            <ArgosButton disabled={isSaveDisabled} onClick={() => addSensorLocation()}
                         style={{top: 0, left: 0, position: 'absolute', zIndex: 1000}}>save sensor location
            </ArgosButton>}
          <img ref={imageElement} src={'/AdasProgramsVehicleRotated.png'} onClick={onClickVehicleImage} style={VEHICLE_IMAGE_SIZE}/>
          {visibleSensorLocations && Object.keys(visibleSensorLocations).map((sensorLocationKey, index) => {
            const sensorLocation = visibleSensorLocations[sensorLocationKey]
            const multRelativeXPercent = sensorLocation.coordinates.x
            let multRelativeYPercent = sensorLocation.coordinates.y
            const elementKey = sensorLocationKey === '' ? 'pending' : sensorLocationKey
            return (
              <div
                key={elementKey}
                style={{
                  position: 'absolute',
                  top: `${multRelativeYPercent}%`,
                  left: `${multRelativeXPercent}%`,
                  zIndex: 1000,
                }}>
                <SensorLocation
                  key={`${elementKey}-SensorLocation`}
                  locationKey={sensorLocationKey} setPendingSensorLocationKey={setPendingSensorLocationKey}
                  sensorLocations={visibleSensorLocations}
                  deleteSensorLocation={deleteSensorLocation}
                  addSensorToLocation={addSensorToLocation}
                  sensorsData={sensorsData}
                />
              </div>
            )
          })}
        </div>
      </BoxWithCenteredContent>

      <ArgosDialog
        width={'60vw'}
        height={'80vh'}
        open={isEditSensorDialogOpen}
        onClose={() => dispatch(setConfigSensorWizardState(null))}
        headerTitle={wizardState?.action === EAdasProgramsEditSensorDialogType.EDIT_FROM_LOCATION ||
        wizardState?.action === EAdasProgramsEditSensorDialogType.EDIT_FROM_TABLE ? 'Edit Sensor Wizard' : 'Add Sensor Wizard'}
      >
        <ConfigSensorWizard/>
      </ArgosDialog>
    </>
  )
}

interface IStyledProps {
  disableHoverUi?: boolean;
}

const StyledPopupItem = styled.div<IStyledProps>(props => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: props.disableHoverUi ? 'transparent' : COLORS.x11Gray,
  }
}));

const StyledSensorIcon = styled.div({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: COLORS.x11Gray,
  }
})

const SensorIcon = React.memo((props: any) => {
  const dispatch = useAppDispatch()
  const {sensorId, sensorName, sensorData, icon, locationKey, sensorType, version, displayName, processedResolution, notSourced, notes} = props
  const modality = sensorType
  const name = sensorName
  const id = sensorId
  const wizardState: ConfigSensorWizardState = {
    action: EAdasProgramsEditSensorDialogType.EDIT_FROM_LOCATION,
    selectSensorStep: {
      ...defaultSelectSensorStepData,
      locationKey: [locationKey], oldLocationKey: [locationKey], oldId: id,
      modality, name, version, id, processedResolution, notSourced, notes,
    },
    definePropertiesStep: defaultDefinePropertiesStepData,
  }

  const adasProgramId = useAppSelector(state => selectSensorBomData(state)?.adasProgramId)

  const removeSensorFromLocationHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=removeSensorFromLocation')
  const removeSensorFromLocation = useCallback((sensorId: string, locationKey: string[], adasProgramId: string) => {
    // TODO: editing single sensor at location
    removeSensorFromLocationHandle({adasProgramId, locationKey, sensorId})
      .then((res: any) => {
        dispatch(setSensorBomData(res))
      }).catch((err: any) => {
      console.log(err)
      toast.error('Error removing sensor')
    })
  }, [removeSensorFromLocationHandle])

  const hasWriteAccess = useHasWriteAccess()
  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const reduxIsSensorBomReadOnly = useAppSelector(state => selectSensorBomState(state).isReadOnly?.[adasProgramId]) ?? false
  const isSensorBomReadOnly = reduxIsSensorBomReadOnly || !hasWriteAccess || reduxIsProgramArchive
  return (
    <PopupState variant="popover" popupId="sensor-icon-popup">
      {(popupState) => (
        <div>
          <StyledSensorIcon {...bindHover(popupState)}>
            {icon}
          </StyledSensorIcon>
          <HoverPopover
            {...bindPopover(popupState)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            // hidden={!hasWriteAccess}
          >
            <div style={{padding: '0px 5px 5px 5px'}}>
              <div style={{fontWeight: 600}}>{displayName}</div>
              {!isSensorBomReadOnly &&
                <>
                  <StyledPopupItem onClick={() => dispatch(setConfigSensorWizardState(wizardState))}>
                    Edit Sensor
                  </StyledPopupItem>
                  <StyledPopupItem onClick={() => removeSensorFromLocation(sensorId, [locationKey], adasProgramId)}>
                    Delete Sensor
                  </StyledPopupItem>
                </>
              }
            </div>
          </HoverPopover>
        </div>
      )}
    </PopupState>
  )
})

const calculatePosition = (index: number, offset: number, extraOffset: number,
                           direction: string, inputWidth: number,
                           inputHeight: number, iconHeight: number,
                           slope: number = .8, diagonalSpacing: number = 2) => {

  let baseOffset = extraOffset

  const horizontalCenter = inputWidth / 2 - (iconHeight / 2);
  const verticalCenter = inputHeight / 2 - (iconHeight / 2);

  const adjustedOffsetX = (offset + diagonalSpacing) * Math.cos(slope);
  const adjustedOffsetY = (offset + diagonalSpacing) * Math.sin(slope);

  const diagonalDirections = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
  const topDiagonals = ['top-left', 'top-right']
  const bottomDiagonals = ['bottom-left', 'bottom-right']
  const isDiagonal = diagonalDirections.includes(direction)
  if (isDiagonal) {
    if (topDiagonals.includes(direction)) baseOffset = -3
    if (bottomDiagonals.includes(direction)) baseOffset = -4
  }

  switch (direction) {
    case 'top-left':
      return {
        top: `${-index * adjustedOffsetY - baseOffset - iconHeight}px`,
        left: `${-index * adjustedOffsetX - baseOffset - iconHeight}px`
      };
    case 'top-right':
      return {
        top: `${-index * adjustedOffsetY - baseOffset - iconHeight}px`,
        left: `${index * adjustedOffsetX + baseOffset + inputWidth}px`
      };
    case 'bottom-left':
      return {
        top: `${index * adjustedOffsetY + baseOffset + inputHeight - 8}px`,
        left: `${-index * adjustedOffsetX - baseOffset - iconHeight}px`
      };
    case 'bottom-right':
      return {
        top: `${index * adjustedOffsetY + baseOffset + inputHeight}px`,
        left: `${index * adjustedOffsetX + baseOffset + inputWidth}px`
      };
    case 'right':
      return {
        top: `${verticalCenter}px`,
        left: `${index * offset + baseOffset + inputWidth}px`
      };
    case 'left':
      return {
        top: `${verticalCenter}px`,
        left: `${-index * offset - baseOffset - iconHeight}px`
      };
    case 'top':
      return {
        top: `${-index * offset - baseOffset - iconHeight - 5}px`,
        left: `${horizontalCenter}px`
      };
    case 'bottom':
      return {
        top: `${index * offset + baseOffset + inputHeight}px`,
        left: `${horizontalCenter}px`
      };
    default:
      return {
        top: `${index * offset + baseOffset}px`,
        left: `${index * offset + baseOffset}px`
      };
  }
};

const SensorLocation = React.memo((props: any) => {
  const {locationKey, sensorLocations, sensorsData} = props
  const dispatch = useAppDispatch()
  const currentLocation = sensorLocations[locationKey]
  const [inputValue, setInputValue] = useState(locationKey ?? '')
  const editLocationsEnabled = useAppSelector(state => state.adasPrograms.isEditLocationsEnabled)
  const listOfSensorsAtLocation = currentLocation?.sensors ?? []
  const sensorBomVisibleSensorsCheckboxes: any = useAppSelector(state => state.adasPrograms.sensorBom.sensorBomVisibleSensorsCheckboxes)

  const inputRef = useRef<any>(null);

  const [inputSize, setInputSize] = useState({width: 0, height: 0});

  useEffect(() => {
    // Function to update the state with the current dimensions
    const updateDimensions = () => {
      const width = inputRef.current ? inputRef.current.offsetWidth : 0;
      const height = inputRef.current ? inputRef.current.offsetHeight : 0;
      // Assuming you have a state setter function for setting these dimensions
      setInputSize({width, height});
    };

    // Call once to set initial dimensions
    updateDimensions();

  }, []); // Empty dependency array means this effect runs once on mount

  const sensorLogos = useMemo(() => {
    // const inputWidth = inputRef.current ? inputRef.current.offsetWidth : 0;
    // const inputHeight = inputRef.current ? inputRef.current.offsetHeight : 0;
    const inputWidth = inputSize.width
    const inputHeight = inputSize.height
    if (!inputWidth || !inputHeight) return []
    return listOfSensorsAtLocation.map((sensorId: string, index: number) => {
      if (!sensorsData) return null
      const sensor = sensorsData[sensorId]
      if (!sensor) return null
      const sensorType = sensor.sensorType
      if (!sensorType) return null
      const sensorName = sensor.sensorName
      if (!sensorName) return null
      const version = sensor.version
      const displayName = sensor.displayName
      const notSourced = sensor.notSourced ?? false
      if (!sensorBomVisibleSensorsCheckboxes[sensorType].checked) return null
      const sensorIconSize = '20px'
      let icon = getSensorIcon(sensorType, sensorIconSize);
      const defaultDirection = 'left'
      const direction = currentLocation?.direction ?? defaultDirection
      const iconHeight = 20
      const offset = 20
      const extraOffset = 0
      const slope = currentLocation?.slope
      const diagonalOffset = currentLocation?.diagonalOffset
      const position = calculatePosition(index, offset, extraOffset, direction,
        inputWidth, inputHeight, iconHeight, slope, diagonalOffset);
      return (
        <div key={index} style={{position: 'absolute', top: position.top, left: position.left}}>
          <SensorIcon icon={icon} sensorData={sensor} sensorId={sensorId} sensorName={sensorName} sensorType={sensorType} version={version} displayName={displayName} notSourced={notSourced}
                      locationKey={locationKey}/>
        </div>
      );
    })
  }, [sensorsData, sensorLocations, locationKey, sensorBomVisibleSensorsCheckboxes, inputSize]);
  const adasProgramId = useAppSelector(state => selectSensorBomData(state)?.adasProgramId)
  const shouldShowSensorLocations = sensorBomVisibleSensorsCheckboxes[EAdasProgramSensorBomCheckboxTypes.SENSOR_LOCATIONS].checked

  const moveLocationsEnabled = useAppSelector(state => selectMoveSensorLocationsState(state).isMoveLocationsEnabled)
  const moveLocationKey = useAppSelector(state => selectMoveSensorLocationsState(state).locationKey)
  const isInputOrClickDisabled = (moveLocationsEnabled && moveLocationKey !== locationKey)
    || (!editLocationsEnabled && !moveLocationsEnabled)
    || (editLocationsEnabled && sensorLocations[locationKey]?.sensorLocationState === 'saved')

  useEffect(() => {
    if (inputRef.current)
      inputRef.current.focus()
  }, [moveLocationKey]);

  // make the input ref initialize so that we'll have the height and width of the input
  useEffect(() => {
    if (inputRef.current)
      inputRef.current.focus()
  }, [inputRef.current]);

  const onClickLocation = (e: any) => {
    e.preventDefault();
    dispatch(setMoveLocationKey(locationKey))
    if (e.target !== inputRef.current) {
      inputRef.current.focus();
      inputRef.current.click();
    }
  }

  const hasWriteAccess = useHasWriteAccess()
  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const reduxIsSensorBomReadOnly = useAppSelector(state => selectSensorBomState(state).isReadOnly?.[adasProgramId]) ?? false
  const isSensorBomReadOnly = (reduxIsSensorBomReadOnly || !hasWriteAccess) || reduxIsProgramArchive
  return (
    <PopupState variant="popover" popupId="demo-popup-popover" disableAutoFocus={false}>
      {(popupState) => (
        <div style={{height: 'auto'}}>
          <div style={{display: 'flex'}}>
            {shouldShowSensorLocations &&
              <div style={{position: 'relative', width: 'min-content', paddingRight: 2}}>
                <div style={{display: 'inline-block', position: 'relative'}}>
                  <span id={'size-calibration'} style={{visibility: 'hidden', whiteSpace: 'pre', paddingRight: '0.5em'}}>
                    {inputValue}
                  </span>
                  <input
                    ref={inputRef}
                    disabled={isInputOrClickDisabled}
                    id={'autosized-input'}
                    {...bindHover(popupState)}
                    style={{position: 'absolute', left: 0, width: '100%', fontWeight: 700}}
                    value={inputValue}
                    defaultValue={''}
                    onChange={e => {
                      setInputValue(e.target.value);
                      props.setPendingSensorLocationKey(locationKey, e.target.value);
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 0,
                      bottom: 0,
                      background: 'transparent', // Make it invisible,
                      pointerEvents: ADAS_PROGRAMS_CODE_CONFIG.ADMIN_MODIFIERS ? 'auto' : 'none',
                    }}
                    onClick={onClickLocation}
                  ></div>
                  {/* Icons */}
                  {
                    sensorLogos
                  }
                </div>
              </div>
            }
            {/*<div style={{display: 'flex', gap: 1}}>*/}
            {/*  {sensorLogos}*/}
            {/*</div>*/}
          </div>

          <HoverPopover
            {...bindPopover(popupState)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            hidden={isSensorBomReadOnly}
          >
            <div style={{padding: 2}}>
              <StyledPopupItem onClick={() => props.addSensorToLocation(locationKey, currentLocation)}>
                Add Sensor
              </StyledPopupItem>
              {ADAS_PROGRAMS_CODE_CONFIG.ADMIN_MODIFIERS &&
                <StyledPopupItem onClick={() => props.deleteSensorLocation(locationKey)}>
                  Delete Sensor Location
                </StyledPopupItem>}
            </div>
          </HoverPopover>
        </div>
      )}
    </PopupState>
  );
})